/**
 * CredentialsCheatsheetCard — Admin → Overview.
 *
 * One-click download of the AES-256 encrypted PDF cheat-sheet of every
 * staff/admin/super-admin/demo account credential. Useful right after a
 * password rotation — admin clicks "Download cheat-sheet" and gets a
 * fresh PDF without SSH'ing into the box.
 *
 * Backend: POST /api/admin/credentials/cheatsheet (admin-gated)
 * The PDF itself is encrypted with open password "ZHELTA-2026". Both the
 * file in transit and the file at rest are useless without the password.
 */
import React, { useState } from 'react';
import { FileDown, Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${(process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const ADMIN_PW_KEY = 'zhelta_admin_pw';

function authHeaders() {
  let pw = '';
  try { pw = localStorage.getItem(ADMIN_PW_KEY) || ''; } catch { /* ignore */ }
  return pw ? { 'X-Admin-Password': pw } : {};
}

export default function CredentialsCheatsheetCard() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const download = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch(`${API}/admin/credentials/cheatsheet`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status} — ${(await r.text()).slice(0, 120)}`);
      // Open password is sent as a response header so we don't have to
      // hardcode it in the frontend (rotates from env). Falls back to a
      // friendly note if header isn't readable for some reason.
      const openPw = r.headers.get('X-PDF-Open-Password') || '(see /app/backend/.env CHEATSHEET_PDF_PASSWORD)';
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhelta_credentials_cheatsheet_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Copy password to clipboard so admin can paste straight into the
      // PDF reader instead of squinting at this banner.
      try { await navigator.clipboard.writeText(openPw); } catch { /* ignore */ }
      setMsg({ ok: true, text: `Downloaded ✓ Open password copied to clipboard: ${openPw}` });
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Download failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="admin-credentials-cheatsheet"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap bg-gradient-to-r from-zinc-950 to-zinc-800 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <Lock className="w-4 h-4 text-[#f5a623] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#f5a623]">Security · Credentials</div>
            <h3 className="text-[15px] font-bold truncate">Team cheat-sheet (offline-ready PDF)</h3>
            <p className="text-[11px] text-white/60 mt-0.5">
              All 4 staff accounts + 3 academy demos, AES-256 encrypted. Re-download after every password rotation.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          data-testid="credentials-cheatsheet-download"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[11px] disabled:opacity-50 hover:opacity-90 transition-opacity shrink-0"
          style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
        >
          <FileDown className="w-3.5 h-3.5" />
          {busy ? 'Generating…' : 'Download cheat-sheet'}
        </button>
      </div>

      {msg && (
        <div className={`px-6 py-2.5 text-[11.5px] font-bold border-t ${msg.ok ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'}`}>
          {msg.ok ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> : <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
          {msg.text}
        </div>
      )}

      <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 text-[10.5px] text-amber-900 leading-relaxed">
        <strong>Handling rules:</strong> never paste contents into Slack/email/screenshots. Don't commit the PDF to Git.
        Keep printed copies in a locked drawer; shred after the next rotation. Demo passwords are intentionally
        guessable (sandboxed data).
      </div>
    </section>
  );
}
